import { formatCalendarDay, subtractMonths, toCalendarDay } from './calendarDay';
import type { ControlPeriod, ControlPeriodConfig } from './types';

/**
 * Resolve a control's period from its type's configuration and its date.
 *
 * Steps:
 * 1. Anchor on the control date, or on `today` when the control has none
 *    (`isEstimated` is then true — the header says "preliminärt").
 * 2. CALENDAR_YEAR: the main year is the anchor's year plus the offset, and the
 *    period is that whole calendar year.
 *    ROLLING_MONTHS: the period ends on the anchor and starts `periodMonths`
 *    months earlier; the main year is the anchor's year and `years` lists every
 *    calendar year the span touches.
 * 3. `referenceDate` is the anchor in both modes: the day an expiring document
 *    must still be valid on.
 *
 * @param config The control type's period configuration.
 * @param scheduledDate The control date as `YYYY-MM-DD`, an ISO string or a Date (UTC day), or null.
 * @param today The calendar day to fall back on. Callers decide whose "today" that is.
 * @returns The resolved period, dates as `YYYY-MM-DD`.
 * @throws Error when rolling mode has no `periodMonths` — a configuration the API never writes.
 */
export function resolveControlPeriod(
  config: ControlPeriodConfig,
  scheduledDate: string | Date | null | undefined,
  today: string | Date,
): ControlPeriod {
  const isEstimated = !scheduledDate;
  const anchor = toCalendarDay(scheduledDate ?? today);
  const referenceDate = formatCalendarDay(anchor);

  if (config.periodMode === 'ROLLING_MONTHS') {
    if (!config.periodMonths || config.periodMonths < 1) {
      throw new Error('ROLLING_MONTHS requires periodMonths >= 1');
    }
    const start = subtractMonths(anchor, config.periodMonths);
    const years: number[] = [];
    for (let y = start.year; y <= anchor.year; y++) years.push(y);
    return {
      mode: 'ROLLING_MONTHS',
      startDate: formatCalendarDay(start),
      endDate: referenceDate,
      mainYear: anchor.year,
      years,
      referenceDate,
      isEstimated,
    };
  }

  const mainYear = anchor.year + config.periodYearOffset;
  return {
    mode: 'CALENDAR_YEAR',
    startDate: `${mainYear}-01-01`,
    endDate: `${mainYear}-12-31`,
    mainYear,
    years: [mainYear],
    referenceDate,
    isEstimated,
  };
}

/**
 * The concrete years a requirement's documents must cover: `mainYear + offset`
 * for every offset, ascending and deduplicated. Empty offsets mean the
 * requirement has no year window configured.
 */
export function requirementYears(mainYear: number, yearOffsets: readonly number[]): number[] {
  return Array.from(new Set(yearOffsets.map((offset) => mainYear + offset))).sort((a, b) => a - b);
}

/**
 * The offsets that reproduce the pre-AG-298 "N months ⇒ ceil(N/12) years back
 * from the main year" window: 36 → [0, -1, -2]. Used to show an admin what an
 * old value meant; the migration applies the same formula in SQL.
 */
export function yearOffsetsFromMonths(months: number): number[] {
  const yearCount = Math.max(1, Math.ceil(months / 12));
  // `0 - i`, not `-i`: `-0` is a distinct value to Object.is and to toEqual.
  return Array.from({ length: yearCount }, (_, i) => 0 - i);
}
